import { useParams } from 'react-router-dom';

import { GridRowModel } from '@mui/x-data-grid';
import { useAppSelector } from 'app/hooks';
import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { selectPageInfo } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { Roles } from 'common/constants/roles';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { GetDeleteDialogContent } from 'features/aclEntries/AclEntriesDeleteDialogContent';
import { GetDrawerContent } from 'features/aclEntries/AclEntriesDrawerContent';

import {
    AclEntiriesDeleteDialogProperties,
    AclEntriesDrawerProperties,
    AclEntriesPage
} from '../aclEntries/aclEntriesTypeDefs';

import { addNewFilter, clearFilters, removeFilter, selectFilters } from './aclEntriesSlice';
import {
    _createUserGroupAclEntriesActionDefinitions,
    _createUserGroupAclEntriesDataGridColumns
} from './helpers';
import { useFetchUserGroupAclEntriesDefaultDataset } from './hooks/useFetchUserGroupAclEntries';
import { useFetchUserGroupAclEntriesDetailsWithLicenseInfo } from './hooks/useFetchUserGroupAclEntriesWithLicense';

export const UserGroupAclEntriesTab = () => {

    const typeName = 'ACL Entry';
    const [sortModel, setSortModel] = usePalmSortModel('resourceTableName');
    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);
    const pageInfo = useAppSelector(selectPageInfo);
    const { tenantId = '', usergroupId, userId } = useParams();

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters);

    const {
        userGroupaclEntriesDefaultDatasetProcessing,
        refreshUserGroupAclEntriesDefaultDataset
    } = useFetchUserGroupAclEntriesDefaultDataset({
        tenantId,
        usergroupId,
        userId,
        isLicenseRequired: pageInfo?.tenantRequiresLicense
    });

    const {
        userGroupAclEntriesWithLicenseInfoProcessing,
        refreshUserGroupAclEntriesDetailsWithLicenseInfo
    } = useFetchUserGroupAclEntriesDetailsWithLicenseInfo({
        tenantId,
        usergroupId,
        userId,
        isLicenseRequired: pageInfo?.tenantRequiresLicense
    });

    const refreshUserGroupAclEntriesDataGridCallback = pageInfo?.tenantRequiresLicense
        ? refreshUserGroupAclEntriesDetailsWithLicenseInfo
        : refreshUserGroupAclEntriesDefaultDataset;

    const disabledEditCallback = (row: GridRowModel) => {
        return shouldDisableButtons || pageInfo?.tenantRequiresLicense != true && row.conditions?.length == 0;
    };
    const columnDefinitions = _createUserGroupAclEntriesDataGridColumns(pageInfo?.tenantRequiresLicense);
    const actionDefinitions = _createUserGroupAclEntriesActionDefinitions(
        shouldDisableButtons,
        disabledEditCallback
    );

    function GetTenantUserGroupAclDrawer() {
        const drawerProps : AclEntriesDrawerProperties = {
            tenantId: pageInfo?.tenantId ?? '',
            tenantName: pageInfo?.tenantName ?? '',
            tenantRequiresLicense: pageInfo?.tenantRequiresLicense ?? false,
            principalId: pageInfo?.id ?? '',
            principalName: pageInfo?.name ?? '',
            aclPage: AclEntriesPage.TenantUserGroup
        };
        return GetDrawerContent(drawerProps);
    }

    function GetAclDeleteDialogContent() {
        const dialogProps: AclEntiriesDeleteDialogProperties = {
            aclPage: AclEntriesPage.TenantUserGroup,
            principalName: pageInfo?.name ?? '',
            principalId: pageInfo?.id ?? '',
            tenantRequiresLicense: pageInfo?.tenantRequiresLicense
        };
        return GetDeleteDialogContent(dialogProps);
    }

    return <PalmDataGrid
        deleteFunction={GetAclDeleteDialogContent()}
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        title={typeName}
        isCreateButtonDisabled={shouldDisableButtons}
        processing={
            pageInfo?.tenantRequiresLicense ?
                userGroupAclEntriesWithLicenseInfoProcessing
                : userGroupaclEntriesDefaultDatasetProcessing
        }
        filter={filterProp}
        contentFunction={GetTenantUserGroupAclDrawer()}
        refreshDataGridClick={refreshUserGroupAclEntriesDataGridCallback}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;
};
